// customer-contact-management.component.ts
import {Component, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {AuthService, DotLoadingServices} from '../../../../../../core';
import {NotificationService} from "../../../../../services/notification.service";
import {CustomerManagementService} from '../../services/customers.service';
import {CustomerContactPersonDTO} from '../../interfaces/customer.entity';
import {SearchPersonTitleComponent} from '../../../../../shared';
import {ContactPersonService} from '../../../Shared/services/contact-person.service';
import {CustomerModalStateService} from '../../services/customer-modal-state.service';
import {PhoneNumberOnlyDirective} from '../../../Shared/Directives/phone-numbers-only.directive';

@Component({
  selector: 'app-customer-contact-management',
    imports: [
        FormsModule,
        SearchPersonTitleComponent,
        PhoneNumberOnlyDirective,
    ],
  templateUrl: './customer-contact-management.component.html',
  styleUrl: './customer-contact-management.component.scss',
  standalone: true
})
export class CustomerContactManagementComponent implements OnInit, OnDestroy {
  
  protected customerService = inject(CustomerManagementService);
  protected authService = inject(AuthService);
  protected contactPersonService = inject(ContactPersonService)
  protected notification = inject(NotificationService);
  protected loading = inject(DotLoadingServices);
  protected router = inject(Router);
  protected modalState = inject(CustomerModalStateService);
  resetSearchTrigger = signal<boolean>(false);
  protected currentCustomerId = signal<number | null>(null);
  protected customerName = signal<string>('');
  
  // Separate arrays for existing and new contact persons
  protected existingContacts = signal<CustomerContactPersonDTO[]>([]);
  protected newContacts = signal<CustomerContactPersonDTO[]>([]);

  ngOnInit(): void {
    const activeCustomer = this.customerService.active();
    if (!activeCustomer) {
      this.notification.set({
        type: 'alert',
        message: 'No customer selected. Redirecting...',
        timeout: 3000
      });
      this.router.navigate(['/customer/customer-grid']);
      return;
    }

    this.currentCustomerId.set(activeCustomer.customerId);    
    this.customerName.set(activeCustomer.customerName);
    this.loadExistingContacts(activeCustomer);
  }

  private loadExistingContacts(customer: any): void {
    const contacts = this.mapContactPersonsFromAPI(customer.contactPersons || []);    
    this.existingContacts.set(contacts);
  }

  private mapContactPersonsFromAPI(contactPersons: any[]): CustomerContactPersonDTO[] {
    return contactPersons.map(cp => ({
      contactPersonId: cp.contactPersonId,
      title: cp.titleId || -1,
      contactPersonName: cp.contactPersonName || '',
      contactPersonDesignation: cp.contactPersonDesignation || '',
      contactPersonOfficePhoneNumber: cp.contactPersonOfficePhoneNumber || '',
      contactPersonMobileNumber: cp.contactPersonMobileNumber || '',
      contactPersonEmail: cp.contactPersonEmail || '',
      status: cp.status || ''
    }));
  }

  // Add new contact person to the new contacts list
  protected addNewContact(): void {
    const newContact: CustomerContactPersonDTO = {
      title: -1,
      contactPersonName: '',
      contactPersonDesignation: '',
      contactPersonOfficePhoneNumber: '',
      contactPersonMobileNumber: '',
      contactPersonEmail: '',
      status:true
    };
    this.newContacts.update(current => [...current, newContact]);
  }

  protected removeNewContact(index: number): void {
    this.newContacts.update(current => current.filter((_, i) => i !== index));
  }

  protected updateNewContactField(index: number, field: keyof CustomerContactPersonDTO, value: any): void {
    this.newContacts.update(current => {
      const updated = [...current];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }

  protected onNewContactTitleSelected(index: number, value: any): void {
    this.updateNewContactField(index, 'title', value);
  }

  protected isValidNewContacts(): boolean {
    const contacts = this.newContacts();
    
    if (contacts.length === 0) {
      return true; // Disabled if no new contacts
    }

    return !contacts.every(contact => {
      return contact.contactPersonName?.trim() && contact.title !== -1;
    });
  }

  protected async saveNewContacts(): Promise<void> {
    if (this.isValidNewContacts()) {
      this.notification.set({
        type: 'alert',
        message: 'Please fill all required fields for new contact persons.',
        timeout: 3000
      });
      return;
    }

    const customerId = this.currentCustomerId();
    if (!customerId) {
      this.notification.set({
        type: 'alert',
        message: 'Customer ID not found.',
        timeout: 3000
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = this.newContacts().filter(
      contact => contact.contactPersonEmail && !emailRegex.test(contact.contactPersonEmail)
    );
    
    if (invalidEmails.length > 0) {
      this.notification.set({
        type: 'alert',
        message: 'Please enter valid email addresses for all contact persons.',
        timeout: 3000
      });
      return;
    }

    try {
      this.loading.set(true);

      const formattedContacts = this.newContacts().map(contact => ({
        title: contact.title,
        contactPersonName: contact.contactPersonName,
        contactPersonDesignation: contact.contactPersonDesignation || '',
        contactPersonOfficePhoneNumber: contact.contactPersonOfficePhoneNumber || '',
        contactPersonMobileNumber: contact.contactPersonMobileNumber || '',
        contactPersonEmail: contact.contactPersonEmail || ''
      }));

      const payload = {
        customerId: customerId,
        createdBy: this.authService.username(),
        contactPersons: formattedContacts
      };

      await firstValueFrom(
        this.contactPersonService.create(payload)
      );

      this.notification.set({
        type: 'success',
        message: 'New contact persons added successfully.',
      });

      // Refresh the customer data and reload
      await this.refreshCustomerData();

    } catch (error) {
      this.notification.set({
        type: 'error',
        message: 'Failed to add new contact persons.',
      });
      console.error(error);
    } finally {
      this.loading.set(false);
    }
  }

  private async refreshCustomerData(): Promise<void> {
    const customerId = this.currentCustomerId();
    if (!customerId) return;

    try {
      const customer = await firstValueFrom(
        this.customerService.getById(customerId)
      );      
      this.loadExistingContacts(customer.data);
      this.newContacts.set([]);
      // this.customerService.notifyCustomerCreated();
    } catch (error) {
      console.error('Failed to refresh customer data:', error);
    }
  }

  protected goBack(): void {
 this.modalState.updateModal.set(true);
    this.modalState.generalUpdateModal.set(true);
    this.modalState.contactPersonspdateModal.set(false);
    this.modalState.addressesUpdateModal.set(false);  }

    toggleContactStatus(contact: any): void {
  const newStatus = contact.status ? 0 : 1;
    this.updateContactStatus(contact.contactPersonId, newStatus);
}

// Add this method to call the API
async updateContactStatus(contactPersonId: number, isActive: number) {

      try {
      this.loading.set(true);

  const payload = {
    isActive: isActive,
    updatedBy: this.authService.username(),
  };

      await firstValueFrom(
        this.contactPersonService.toggleContactPersonStatus(contactPersonId,payload)
      );

      this.notification.set({
        type: 'success',
        message: 'Contact persons Status changed successfully.',
      });

      // Refresh the customer data and reload
      await this.refreshCustomerData();

    } catch (error) {
      this.notification.set({
        type: 'error',
        message: 'Failed to change Contact persons Status.',
      });
      console.error(error);
    } finally {
      this.loading.set(false);
    }

}

  ngOnDestroy(): void {
    this.newContacts.set([]);
    this.resetSearchTrigger.set(false);
  }
}